// src/pages/PrizeList.js
import React, { useEffect, useState } from "react";
import { getAllPrizes, uploadPrizeFile, deleteAllPrizes } from "../services/api"; // Import the API functions

const PrizeList = () => {
  const [prizes, setPrizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for file upload
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  // State for delete confirmation
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Fetch all prizes
 useEffect(() => {
    fetchPrizes();
  }, []);

  const fetchPrizes = async () => {
    try {
      setLoading(true);
      const response = await getAllPrizes();
      console.log(response);
      setPrizes(response.prizes || response || []);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch prize list");
    } finally {
      setLoading(false);
    }
  };

  // Handle file selection
  const handleFileChange = (e) => {
    setSelectedFile(e.target.files?.[0] || null);
    setError(null);
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!selectedFile) {
      setError("Choose a file before uploading.");
      return;
    }

    try {
      setUploading(true);
      setProgress(0);

      const formData = new FormData();
      formData.append("file", selectedFile);

      await uploadPrizeFile(formData, (event) => {
        const percent = Math.round((event.loaded / event.total) * 100);
        setProgress(percent);
      });

      setSelectedFile(null);
      await fetchPrizes(); // Refresh the list after upload
    } catch (err) {
      setError("Failed to upload prize file");
      console.error(err);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  // Handle delete confirmation initiation
  const handleDeleteAll = () => {
    setDeleteConfirmation(true);
  };

  // Confirm deletion of all prizes
  const confirmDeleteAll = async () => {
    try {
      setDeleting(true);
      await deleteAllPrizes();
      setPrizes([]);
      setDeleteConfirmation(false);
    } catch (err) {
      setError("Failed to delete prizes");
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  // Cancel deletion process
  const cancelDelete = () => {
    setDeleteConfirmation(false);
  };

  if (loading) return <p>Loading prize list...</p>;

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Prize List</h1>

      {error && <p style={styles.errorText}>{error}</p>}

      <div style={styles.uploadBox}>
        <label style={styles.uploadLabel}>Upload Excel File:</label>
        <div style={styles.fileInputWrapper}>
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileChange}
            style={styles.fileInput}
          />
        </div>

        <div style={styles.buttonRow}>
          <button onClick={handleDeleteAll} style={styles.deleteButton} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </button>

          <button onClick={handleUpload} style={styles.uploadButton} disabled={uploading}>
            {uploading ? "Uploading..." : "Upload"}
          </button>
        </div>
      </div>

      {selectedFile && (
        <p style={styles.selectedFileText}>Selected file: {selectedFile.name}</p>
      )}

      {uploading && (
        <div style={styles.progressWrapper}>
          <div style={{ ...styles.progressBar, width: `${progress}%` }} />
          <span style={styles.progressText}>{progress}%</span>
        </div>
      )}

      {prizes.length === 0 ? (
        <p style={styles.centerText}>No prizes available.</p>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Prize ID</th>
                <th style={styles.th}>Rank</th>
                <th style={styles.th}>Prize Value</th>
              </tr>
            </thead>
            <tbody>
              {prizes.map((prize, index) => (
                <tr
                  key={prize.prize_id}
                  style={index % 2 === 0 ? styles.trEven : styles.trOdd}
                >
                  <td style={styles.td}>{prize.prize_id}</td>
                  <td style={styles.td}>{prize.rank}</td>
                  <td style={styles.td}>{prize.prize_value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Popup */}
      {deleteConfirmation && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modal}>
            <h2>Confirm Deletion</h2>
            <p>Are you sure you want to delete all prize records? This cannot be undone.</p>
            <div style={styles.buttonContainer}>
              <button onClick={confirmDeleteAll} style={styles.deleteButton} disabled={deleting}>
                {deleting ? "Deleting..." : "Yes, Delete"}
              </button>
              <button onClick={cancelDelete} style={styles.cancelButton}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: "20px",
    fontFamily: "Arial, sans-serif",
    maxWidth: "720px",
    margin: "0 auto",
    textAlign: "center",
  },
  title: { marginBottom: "20px", fontSize: "24px" },
  errorText: {
    color: "#dc3545",
    marginBottom: "15px",
    textAlign: "center",
  },
  centerText: {
    textAlign: "center",
  },
  uploadBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    marginBottom: "10px",
    padding: "16px",
    backgroundColor: "#f7f8fa",
    border: "1px solid #e2e4e8",
    borderRadius: "8px",
  },
  uploadLabel: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#333",
    alignSelf: "flex-start",
    marginLeft: "4px",
  },
  fileInputWrapper: {
    width: "100%",
    backgroundColor: "#fff",
    border: "1px solid #d5d8dc",
    borderRadius: "6px",
    padding: "10px 14px",
  },
  buttonRow: {
    display: "flex",
    justifyContent: "center",
    gap: "12px",
  },
  fileInput: {
    width: "100%",
  },
  selectedFileText: {
    fontSize: "13px",
    color: "#555",
    margin: "5px 0 15px",
    textAlign: "center",
  },
  progressWrapper: {
    position: "relative",
    width: "100%",
    maxWidth: "320px",
    height: "18px",
    backgroundColor: "#e9ecef",
    borderRadius: "4px",
    overflow: "hidden",
    marginBottom: "15px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  progressBar: {
    height: "100%",
    backgroundColor: "#28a745",
    transition: "width 0.2s ease",
  },
  progressText: {
    position: "absolute",
    top: 0,
    left: "50%",
    transform: "translateX(-50%)",
    fontSize: "12px",
    lineHeight: "18px",
    color: "#333",
    fontWeight: "bold",
  },
  tableWrapper: {
    marginTop: "20px",
    border: "1px solid #e0e0e0",
    borderRadius: "10px",
    overflow: "hidden",
    boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.06)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "center",
    padding: "14px 16px",
    backgroundColor: "#3b6ef6",
    color: "#fff",
    fontWeight: "700",
    fontSize: "15px",
  },
  trEven: {
    backgroundColor: "#f7f9fc",
  },
  trOdd: {
    backgroundColor: "#ffffff",
  },
  td: {
    padding: "14px 16px",
    borderBottom: "1px solid #eef0f3",
    fontSize: "15px",
    textAlign: "center",
  },
  buttonContainer: {
    marginTop: "15px",
    display: "flex",
    justifyContent: "center",
    gap: "12px",
  },
  uploadButton: {
    backgroundColor: "#007bff",
    color: "#fff",
    border: "none",
    padding: "8px 12px",
    borderRadius: "4px",
    cursor: "pointer",
    transition: "background-color 0.3s",
  },
  deleteButton: {
    backgroundColor: "#dc3545",
    color: "#fff",
    border: "none",
    padding: "8px 12px",
    borderRadius: "4px",
    cursor: "pointer",
    transition: "background-color 0.3s",
  },
  cancelButton: {
    padding: "8px 12px",
    backgroundColor: "#6c757d",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    transition: "background-color 0.3s",
  },
  modalBackdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modal: {
    backgroundColor: "#fff",
    padding: "30px",
    borderRadius: "8px",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.3)",
    width: "400px",
    textAlign: "center",
  },
};

export default PrizeList;